import { call, put, takeLatest } from 'redux-saga/effects';
import * as actionTypes from './_actionTypes';
import * as actions from './_actions';
import AdminProgressTrackingHelper from '../../../../utils/API/BackendHelper/admin/progressTracking';
import { buildReportFilename } from '../../../../utils/Export/exportUtils';

function* fetchStudentProgressSaga(action) {
  try {
    const filters = action.payload || {};

    if (!filters.collegeId) {
      yield put(actions.fetchStudentProgressSuccess({ students: [], pagination: {}, summary: {} }));
      return;
    }

    const params = {
      collegeId: filters.collegeId,
      page: filters.page || 1,
      limit: filters.limit || 20,
    };

    if (filters.courseId) params.courseId = filters.courseId;
    if (filters.studentId) params.studentId = filters.studentId;
    if (filters.search) params.search = filters.search;
    if (filters.degree && filters.degree !== 'all') params.degree = filters.degree;
    if (filters.batch) params.batch = filters.batch;
    if (filters.semester && filters.semester !== 'all') params.semester = filters.semester;
    if (filters.progressLevel && filters.progressLevel !== 'all') params.progressLevel = filters.progressLevel;

    const response = yield call(AdminProgressTrackingHelper.getStudentsProgress.bind(AdminProgressTrackingHelper), params);

    if (response.success) {
      yield put(actions.fetchStudentProgressSuccess(response.data));
    } else {
      throw new Error(response.message || 'Failed to fetch student progress');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch student progress';
    yield put(actions.fetchStudentProgressFailure(errorMessage));
  }
}

function* exportProgressReportSaga(action) {
  try {
    const { filters, format } = action.payload;

    if (!filters.collegeId) {
      yield put(actions.exportProgressReportFailure('No college selected'));
      return;
    }

    const params = { collegeId: filters.collegeId };
    if (filters.courseId) params.courseId = filters.courseId;
    if (filters.studentId) params.studentId = filters.studentId;
    if (filters.search) params.search = filters.search;
    if (filters.degree && filters.degree !== 'all') params.degree = filters.degree;
    if (filters.batch) params.batch = filters.batch;
    if (filters.semester && filters.semester !== 'all') params.semester = filters.semester;
    if (filters.progressLevel && filters.progressLevel !== 'all') params.progressLevel = filters.progressLevel;

    const response = yield call(AdminProgressTrackingHelper.getStudentsProgress.bind(AdminProgressTrackingHelper), params);

    if (response.success) {
      if (format === 'csv') {
        const csvData = convertToCSV(response.data.students);
        downloadFile(csvData, buildReportFilename('admin_student_progress', params, 'csv'), 'text/csv;charset=utf-8;');
      } else {
        const jsonData = JSON.stringify(response.data, null, 2);
        downloadFile(jsonData, buildReportFilename('admin_student_progress', params, 'json'), 'application/json');
      }
      yield put(actions.exportProgressReportSuccess());
    } else {
      throw new Error(response.message || 'Failed to export report');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to export report';
    yield put(actions.exportProgressReportFailure(errorMessage));
  }
}

function escapeCSV(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function convertToCSV(students) {
  const headers = [
    'Student ID', 'First Name', 'Last Name', 'Father Name', 'Email', 'Phone',
    'Registration No', 'Degree', 'Semester', 'Batch', 'Admission Type',
    'Total Courses', 'Total Contents', 'Completed Contents',
    'Overall Progress (%)', 'Total Time Spent (min)', 'Courses Completed',
  ];
  const rows = students.map(({ student, overall_statistics }) => [
    student.id, student.first_name, student.last_name || '',
    student.father_name || '',
    student.email || '', student.phone || '', student.registration_no || '',
    student.degree || '', student.current_semester || '', student.batch || '', student.admission_type || '',
    overall_statistics.totalCourses, overall_statistics.totalContents,
    overall_statistics.completedContents, overall_statistics.overallProgress,
    overall_statistics.totalTimeSpent, overall_statistics.coursesCompleted,
  ]);
  return [
    headers.map(escapeCSV).join(','),
    ...rows.map(row => row.map(escapeCSV).join(',')),
  ].join('\n');
}

function downloadFile(data, filename, mimeType) {
  const blob = new Blob([data], { type: mimeType });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default function* adminProgressTrackingSaga() {
  yield takeLatest(actionTypes.FETCH_STUDENT_PROGRESS_REQUEST, fetchStudentProgressSaga);
  yield takeLatest(actionTypes.EXPORT_PROGRESS_REPORT_REQUEST, exportProgressReportSaga);
}
